// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import JSZip from 'jszip';

/**
 * Zip entry normalization for panoramic / pole image uploads (AUT-6294).
 *
 * Archives made with macOS Finder ("Compress") carry entries the user never sees: a `__MACOSX/` tree of
 * AppleDouble resource forks (`._name.jpg`), `.DS_Store` files, and usually a wrapper folder when a folder
 * rather than a selection of files was compressed. The pano / pole pipelines expect a flat archive of
 * images, so those entries used to fail validation or be uploaded as junk. This module decides, per entry,
 * whether it is invisible OS noise (dropped silently), a real payload file (validated), or genuinely
 * unsupported (surfaced to the user).
 *
 * Filenames are never altered: the pole image link recipe resolves images by their exact name.
 */

/** Extensions accepted inside a pano / pole zip. Compared lower-cased; the entry keeps its own casing. */
export const SUPPORTED_ZIP_PAYLOAD_EXTENSIONS = ['.jpg', '.jpeg', '.txt'];

const MACOSX_FOLDER = '__MACOSX';
const APPLEDOUBLE_PREFIX = '._';
const IGNORED_BASENAMES = ['.ds_store', 'thumbs.db'];

export interface NormalizedZipEntry {
  /** Path the entry is written under in the rebuilt zip (wrapper folder removed, basename untouched). */
  name: string;
  /** Path of the entry in the source zip; read the content from there. */
  sourceName: string;
}

export interface NormalizedZip {
  /** Payload files that pass validation, in source order. */
  supported: NormalizedZipEntry[];
  /** Source paths of real payload files that fail validation: unsupported extension, or nested deeper than one wrapper folder. */
  unsupported: string[];
  /** Source paths dropped silently: directory entries and OS artifacts. */
  ignored: string[];
  /** The single top-level folder every payload file sat in, when one was stripped. */
  strippedFolder: string | null;
  /** True when the rebuilt zip would differ from the source, i.e. something was ignored or a wrapper folder stripped. */
  needsRezip: boolean;
}

const basenameOf = (path: string): string => path.slice(path.lastIndexOf('/') + 1);

/** Entries created by the OS rather than the user: the `__MACOSX` tree, AppleDouble `._` forks, `.DS_Store`, `Thumbs.db`. */
export const isOsArtifact = (path: string): boolean => {
  if (path === MACOSX_FOLDER || path.startsWith(`${MACOSX_FOLDER}/`)) {
    return true;
  }
  const basename = basenameOf(path);
  return basename.startsWith(APPLEDOUBLE_PREFIX) || IGNORED_BASENAMES.includes(basename.toLowerCase());
};

/** Case-insensitive extension check against SUPPORTED_ZIP_PAYLOAD_EXTENSIONS. */
export const hasSupportedExtension = (path: string): boolean => {
  const lowerCasePath = path.toLowerCase();
  return SUPPORTED_ZIP_PAYLOAD_EXTENSIONS.some((extension) => lowerCasePath.endsWith(extension));
};

/**
 * Classify the entries of a loaded zip (`JSZip#files`). Directory entries and OS artifacts are ignored; when every
 * remaining file sits in the same single top-level folder that folder is stripped; the result is then validated
 * against the supported extensions, with no further nesting allowed.
 */
export const normalizeZipEntries = (files: Record<string, { dir: boolean }>): NormalizedZip => {
  const ignored: string[] = [];
  const payload: string[] = [];

  Object.keys(files).forEach((path) => {
    if (files[path].dir || isOsArtifact(path)) {
      ignored.push(path);
    } else {
      payload.push(path);
    }
  });

  const topLevelSegments = new Set(payload.map((path) => path.split('/')[0]));
  const allInOneFolder = payload.length > 0 && topLevelSegments.size === 1 && payload.every((path) => path.includes('/'));
  const strippedFolder = allInOneFolder ? payload[0].split('/')[0] : null;

  const supported: NormalizedZipEntry[] = [];
  const unsupported: string[] = [];

  payload.forEach((sourceName) => {
    const name = strippedFolder === null ? sourceName : sourceName.slice(strippedFolder.length + 1);
    if (name.includes('/') || !hasSupportedExtension(name)) {
      unsupported.push(sourceName);
    } else {
      supported.push({ name, sourceName });
    }
  });

  return {
    supported,
    unsupported,
    ignored,
    strippedFolder,
    needsRezip: ignored.length > 0 || strippedFolder !== null,
  };
};

/**
 * Build a new archive holding only `entries`, each under its normalized name with its original bytes.
 * The caller serializes it, e.g. `generateAsync({ type: 'blob' })`.
 */
export const buildNormalizedZip = (source: JSZip, entries: NormalizedZipEntry[]): JSZip => {
  const rebuilt = new JSZip();
  entries.forEach(({ name, sourceName }) => {
    rebuilt.file(name, source.files[sourceName].async('uint8array'));
  });
  return rebuilt;
};
